import React from 'react';
import './PoliciesSection.css';

const CANCELLATION_OPTIONS = [
    {
        value: 'flexible',
        label: 'Flexible',
        description: 'Guests can cancel closer to check-in with a higher refund.'
    },
    {
        value: 'moderate',
        label: 'Moderate',
        description: 'Partial refund is available before the cancellation deadline.'
    },
    {
        value: 'strict',
        label: 'Strict',
        description: 'Limited refund after booking or close to check-in.'
    },
    {
        value: 'non_refundable',
        label: 'Non-refundable',
        description: 'Guests do not receive a refund after booking.'
    }
];

const HOUSE_RULE_OPTIONS = [
    'No smoking',
    'No parties or events',
    'Pets not allowed',
    'Pets allowed',
    'No loud music',
    'No outside guests',
    'Couples allowed',
    'Children allowed',
    'Alcohol allowed',
    'Alcohol not allowed',
    'Photography allowed',
    'Government ID required'
];

const CHECKIN_TIME_OPTIONS = [
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM',
    '5:00 PM',
    '6:00 PM',
    '7:00 PM',
    '8:00 PM',
    '9:00 PM',
    '10:00 PM',
    '11:00 PM'
];

const CHECKOUT_TIME_OPTIONS = [
    '6:00 AM',
    '7:00 AM',
    '8:00 AM',
    '9:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '1:00 PM',
    '2:00 PM',
    '3:00 PM',
    '4:00 PM'
];

const DEFAULT_POLICIES = {
    checkInTime: '2:00 PM',
    checkOutTime: '11:00 AM',
    earlyCheckIn: false,
    lateCheckOut: false,
    minStay: 1,
    maxStay: 0,
    advanceBookingDays: 365,
    sameDayBooking: true,
    instantBooking: true,
    cancellation: {
        type: 'moderate',
        freeCancellationDays: 7,
        refundPercent: 100,
        partialRefundPercent: 50,
        nonRefundableAfterCheckIn: true
    },
    houseRules: []
};

const normalizePolicies = (policies = {}) => ({
    ...DEFAULT_POLICIES,
    ...policies,
    cancellation: {
        ...DEFAULT_POLICIES.cancellation,
        ...(policies.cancellation || {})
    },
    houseRules: Array.isArray(policies.houseRules) ? policies.houseRules : []
});

const toNumber = (value, fallback = 0) => {
    if (value === '' || value === null || value === undefined) {
        return fallback;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
};

const PoliciesSection = ({ formData, updateField, canEdit = true }) => {
    const policies = normalizePolicies(formData?.policies);

    const updatePolicy = (field, value) => {
        if (!canEdit) return;

        updateField('policies', {
            ...policies,
            [field]: value
        });
    };

    const updateCancellation = (field, value) => {
        if (!canEdit) return;

        updateField('policies', {
            ...policies,
            cancellation: {
                ...policies.cancellation,
                [field]: value
            }
        });
    };

    const toggleHouseRule = (rule) => {
        if (!canEdit) return;

        const exists = policies.houseRules.includes(rule);

        const updatedRules = exists
            ? policies.houseRules.filter((item) => item !== rule)
            : [...policies.houseRules, rule];

        updatePolicy('houseRules', updatedRules);
    };

    const addCustomRule = () => {
        if (!canEdit) return;

        const value = window.prompt('Enter house rule');

        if (!value?.trim()) return;

        const trimmedValue = value.trim();

        const exists = policies.houseRules.some((rule) => rule.toLowerCase() === trimmedValue.toLowerCase());

        if (exists) return;

        updatePolicy('houseRules', [...policies.houseRules, trimmedValue]);
    };

    return (
        <div className='policies-section'>
            {/* =====================================================
          HEADER
      ====================================================== */}

            <div className='policies-header'>
                <div>
                    <span className='section-eyebrow'>POLICIES & RULES</span>

                    <h2>Policies</h2>

                    <p>
                        Configure check-in, check-out, booking restrictions, cancellation rules and house rules for
                        guests.
                    </p>
                </div>

                <div className='policy-summary'>
                    <div>
                        <strong>{policies.houseRules.length}</strong>
                        <span>house rules</span>
                    </div>

                    <div>
                        <strong>{policies.instantBooking ? 'ON' : 'OFF'}</strong>
                        <span>instant booking</span>
                    </div>
                </div>
            </div>

            {/* =====================================================
          CHECK-IN / CHECK-OUT
      ====================================================== */}

            <div className='policy-card'>
                <div className='policy-card-header'>
                    <div className='policy-card-icon'>🕐</div>

                    <div>
                        <h3>Check-in & Check-out</h3>

                        <p>Define when guests can arrive and when they need to leave.</p>
                    </div>
                </div>

                <div className='policy-form-grid'>
                    <div className='policy-field'>
                        <label>Check-in Time</label>

                        <select
                            value={policies.checkInTime}
                            onChange={(event) => updatePolicy('checkInTime', event.target.value)}
                            disabled={!canEdit}
                        >
                            {CHECKIN_TIME_OPTIONS.map((time) => (
                                <option value={time} key={time}>
                                    {time}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='policy-field'>
                        <label>Check-out Time</label>

                        <select
                            value={policies.checkOutTime}
                            onChange={(event) => updatePolicy('checkOutTime', event.target.value)}
                            disabled={!canEdit}
                        >
                            {CHECKOUT_TIME_OPTIONS.map((time) => (
                                <option value={time} key={time}>
                                    {time}
                                </option>
                            ))}
                        </select>
                    </div>

                    <PolicyToggle
                        title='Early Check-in'
                        description='Allow guests to request an earlier check-in.'
                        checked={Boolean(policies.earlyCheckIn)}
                        onChange={(value) => updatePolicy('earlyCheckIn', value)}
                        disabled={!canEdit}
                    />

                    <PolicyToggle
                        title='Late Check-out'
                        description='Allow guests to request a later check-out.'
                        checked={Boolean(policies.lateCheckOut)}
                        onChange={(value) => updatePolicy('lateCheckOut', value)}
                        disabled={!canEdit}
                    />
                </div>

                {(policies.earlyCheckIn || policies.lateCheckOut) && (
                    <div className='policy-note'>
                        <span>💡</span>

                        <p>
                            Early check-in and late check-out requests can be subject to availability and additional
                            charges.
                        </p>
                    </div>
                )}
            </div>

            {/* =====================================================
          STAY RULES
      ====================================================== */}

            <div className='policy-card'>
                <div className='policy-card-header'>
                    <div className='policy-card-icon'>📅</div>

                    <div>
                        <h3>Stay & Booking Rules</h3>

                        <p>Control how long guests can stay and how far in advance they can make a reservation.</p>
                    </div>
                </div>

                <div className='policy-form-grid three-columns'>
                    <div className='policy-field'>
                        <label>Minimum Stay</label>

                        <div className='input-with-suffix'>
                            <input
                                type='number'
                                min='1'
                                value={policies.minStay}
                                onChange={(event) =>
                                    updatePolicy('minStay', Math.max(1, toNumber(event.target.value, 1)))
                                }
                                disabled={!canEdit}
                            />

                            <span>night(s)</span>
                        </div>
                    </div>

                    <div className='policy-field'>
                        <label>Maximum Stay</label>

                        <div className='input-with-suffix'>
                            <input
                                type='number'
                                min='0'
                                value={policies.maxStay}
                                onChange={(event) => updatePolicy('maxStay', Math.max(0, toNumber(event.target.value)))}
                                disabled={!canEdit}
                            />

                            <span>{policies.maxStay === 0 ? 'no limit' : 'night(s)'}</span>
                        </div>

                        <span className='policy-field-hint'>Use 0 for no maximum stay.</span>
                    </div>

                    <div className='policy-field'>
                        <label>Advance Booking</label>

                        <div className='input-with-suffix'>
                            <input
                                type='number'
                                min='0'
                                value={policies.advanceBookingDays}
                                onChange={(event) =>
                                    updatePolicy('advanceBookingDays', Math.max(0, toNumber(event.target.value)))
                                }
                                disabled={!canEdit}
                            />

                            <span>days</span>
                        </div>
                    </div>
                </div>

                <div className='policy-toggle-list'>
                    <PolicyToggle
                        title='Same-day Booking'
                        description='Allow guests to book on the same day as check-in.'
                        checked={Boolean(policies.sameDayBooking)}
                        onChange={(value) => updatePolicy('sameDayBooking', value)}
                        disabled={!canEdit}
                    />

                    <PolicyToggle
                        title='Instant Booking'
                        description='Confirm eligible bookings automatically without manual approval.'
                        checked={Boolean(policies.instantBooking)}
                        onChange={(value) => updatePolicy('instantBooking', value)}
                        disabled={!canEdit}
                    />
                </div>
            </div>

            {/* =====================================================
          CANCELLATION
      ====================================================== */}

            <div className='policy-card'>
                <div className='policy-card-header'>
                    <div className='policy-card-icon'>↩️</div>

                    <div>
                        <h3>Cancellation Policy</h3>

                        <p>Choose how cancellations and refunds should be handled.</p>
                    </div>
                </div>

                <div className='cancellation-options'>
                    {CANCELLATION_OPTIONS.map((option) => {
                        const selected = policies.cancellation.type === option.value;

                        return (
                            <button
                                type='button'
                                key={option.value}
                                className={`cancellation-option ${selected ? 'selected' : ''}`}
                                onClick={() => updateCancellation('type', option.value)}
                                disabled={!canEdit}
                            >
                                <span className='cancellation-radio'>{selected && <span />}</span>

                                <span className='cancellation-content'>
                                    <strong>{option.label}</strong>

                                    <small>{option.description}</small>
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className='cancellation-details'>
                    {policies.cancellation.type !== 'non_refundable' && (
                        <div className='policy-form-grid'>
                            <div className='policy-field'>
                                <label>Free Cancellation Until</label>

                                <div className='input-with-suffix'>
                                    <input
                                        type='number'
                                        min='0'
                                        value={policies.cancellation.freeCancellationDays}
                                        onChange={(event) =>
                                            updateCancellation(
                                                'freeCancellationDays',
                                                Math.max(0, toNumber(event.target.value))
                                            )
                                        }
                                        disabled={!canEdit}
                                    />

                                    <span>days</span>
                                </div>
                            </div>

                            <div className='policy-field'>
                                <label>Refund Percentage</label>

                                <div className='input-with-suffix'>
                                    <input
                                        type='number'
                                        min='0'
                                        max='100'
                                        value={policies.cancellation.refundPercent}
                                        onChange={(event) =>
                                            updateCancellation(
                                                'refundPercent',
                                                Math.min(100, Math.max(0, toNumber(event.target.value)))
                                            )
                                        }
                                        disabled={!canEdit}
                                    />

                                    <span>%</span>
                                </div>
                            </div>

                            <div className='policy-field'>
                                <label>Partial Refund</label>

                                <div className='input-with-suffix'>
                                    <input
                                        type='number'
                                        min='0'
                                        max='100'
                                        value={policies.cancellation.partialRefundPercent}
                                        onChange={(event) =>
                                            updateCancellation(
                                                'partialRefundPercent',
                                                Math.min(100, Math.max(0, toNumber(event.target.value)))
                                            )
                                        }
                                        disabled={!canEdit}
                                    />

                                    <span>%</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <PolicyToggle
                        title='No Refund After Check-in'
                        description="Bookings cannot be refunded once the guest's stay has started."
                        checked={Boolean(policies.cancellation.nonRefundableAfterCheckIn)}
                        onChange={(value) => updateCancellation('nonRefundableAfterCheckIn', value)}
                        disabled={!canEdit}
                    />
                </div>
            </div>

            {/* =====================================================
          HOUSE RULES
      ====================================================== */}

            <div className='policy-card'>
                <div className='policy-card-header'>
                    <div className='policy-card-icon'>🏠</div>

                    <div>
                        <h3>House Rules</h3>

                        <p>Tell guests what is allowed and what is not allowed at the property.</p>
                    </div>
                </div>

                <div className='house-rules-grid'>
                    {/* presets + custom / older rules (selected; click to remove) */}
                    {[
                        ...HOUSE_RULE_OPTIONS,
                        ...policies.houseRules.filter((rule) => !HOUSE_RULE_OPTIONS.includes(rule))
                    ].map((rule) => {
                        const selected = policies.houseRules.includes(rule);

                        return (
                            <button
                                type='button'
                                key={rule}
                                className={`house-rule ${selected ? 'selected' : ''}`}
                                onClick={() => toggleHouseRule(rule)}
                                disabled={!canEdit}
                            >
                                <span className='house-rule-check'>{selected ? '✓' : ''}</span>

                                <span>{rule}</span>
                            </button>
                        );
                    })}
                </div>

                {canEdit && (
                    <button type='button' className='custom-rule-btn' onClick={addCustomRule}>
                        + Add Custom Rule
                    </button>
                )}

                {policies.houseRules.length > 0 && (
                    <div className='active-rules'>
                        <span>
                            {policies.houseRules.length} rule
                            {policies.houseRules.length !== 1 ? 's' : ''} selected
                        </span>
                    </div>
                )}
            </div>

            {/* =====================================================
          GUEST POLICY SUMMARY
      ====================================================== */}

            <div className='policy-summary-card'>
                <div className='policy-summary-icon'>✓</div>

                <div>
                    <strong>Policy Summary</strong>

                    <p>
                        Guests can check in from <b>{policies.checkInTime}</b> and must check out by{' '}
                        <b>{policies.checkOutTime}</b>. Minimum stay is{' '}
                        <b>
                            {policies.minStay} night
                            {policies.minStay !== 1 ? 's' : ''}
                        </b>
                        .
                        {policies.maxStay > 0 && (
                            <>
                                {' '}
                                Maximum stay is <b>{policies.maxStay} nights</b>.
                            </>
                        )}{' '}
                        Cancellation policy is{' '}
                        <b>
                            {CANCELLATION_OPTIONS.find((item) => item.value === policies.cancellation.type)?.label ||
                                'Moderate'}
                        </b>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   TOGGLE
========================================================= */

const PolicyToggle = ({ title, description, checked, onChange, disabled }) => {
    return (
        <div className='policy-toggle'>
            <div className='policy-toggle-content'>
                <strong>{title}</strong>
                <span>{description}</span>
            </div>

            <label className='policy-switch'>
                <input
                    type='checkbox'
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked)}
                    disabled={disabled}
                />

                <span className='policy-switch-slider' />
            </label>
        </div>
    );
};

export default PoliciesSection;
